import { API_BASE, apiRequest, getAccessToken, ApiError } from "./client";

/**
 * Reports (Sprint 8) — thin client for the NestJS reports endpoints.
 *
 * The backend computes every number from real records (org-scoped) and
 * returns camelCase wire shapes. This module maps them into the exact
 * widget shapes the dashboard pages render, so the UI stays honest:
 * empty arrays/zeros instead of invented metrics.
 */

const STAGE_LABELS = {
  1: "Prospecting",
  2: "Qualification",
  3: "Proposal",
  4: "Negotiation",
  5: "Closed Won",
  6: "Closed Lost",
};

export function formatInrCompact(amount) {
  const n = Number(amount || 0);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}

export function formatLastDataUtc(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "never";
  const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date} ${time} UTC`;
}

function topCompaniesToRegions(topCompanies) {
  const total = (topCompanies || []).reduce((sum, c) => sum + (c.dealValue || 0), 0);
  return (topCompanies || []).map((company, index) => ({
    rank: index + 1,
    region: company.name,
    revenue: company.dealValue,
    growth: null,
    share: total ? Math.round((company.dealValue / total) * 100) : 0,
  }));
}

export const reportsApi = {
  /** Operations (Sprint 14, Part 94 §94.2): reply times, auto-replies, escalations, AI tokens. */
  async getOps({ from, to } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    const data = await apiRequest(`/api/reports/ops${qs.toString() ? `?${qs.toString()}` : ""}`);
    return {
      reply: data?.reply ?? { avgFirstReplyMinutes: null, repliedThreads: 0, totalThreads: 0 },
      autoReplies: data?.autoReplies ?? 0,
      escalations: data?.escalations ?? 0,
      aiTokensInWindow: data?.aiTokensInWindow ?? 0,
      threadsOpenedInWindow: data?.threadsOpenedInWindow ?? 0,
      openThreadsNow: data?.openThreadsNow ?? 0,
    };
  },
  /** Executive Overview widgets (KPIs, trends, top companies/sources). */
  async getExecutive() {
    const data = await apiRequest("/api/reports/executive");

    const kpis = (data.kpis || []).map((kpi) => ({
      title: kpi.title,
      value:
        kpi.id === "totalLeads" || kpi.id === "openTasks"
          ? String(kpi.value)
          : formatInrCompact(kpi.value),
      change: null,
      icon: kpi.icon,
      iconColor: kpi.iconColor,
      trend: "up-good",
      linkTo: kpi.linkTo,
    }));

    return {
      kpis,
      revenueCustomerTrend: data.revenueCustomerTrend || [],
      forecastTrend: (data.forecastTrend || []).map((point) => ({
        month: point.month,
        actual: point.actual,
        forecast: point.forecast,
        upper: null,
        lower: null,
      })),
      topRegions: topCompaniesToRegions(data.topCompanies),
      satisfaction: null,
      topSources: data.topSources || [],
      newLeads: data.newLeads ?? 0,
      newContacts: data.newContacts ?? 0,
      lastDataAtUtc: data.lastDataAtUtc || null,
    };
  },

  /** Sales Performance widgets (per-rep, funnel, pipeline, velocity). */
  async getSales() {
    const data = await apiRequest("/api/reports/sales");

    const pipelineTable = (data.pipelineTable || []).map((row) => ({
      id: row.id,
      opportunityId: row.opportunityId,
      company: row.company,
      contact: row.contact,
      stage: STAGE_LABELS[row.stage] || "Prospecting",
      value: row.value,
      probabilityPercent: row.probabilityPercent,
      closeDateUtc: row.closeDateUtc,
      rep: row.rep || "Unassigned",
      product: row.product || "",
    }));

    const leaderboard = (data.leaderboard || []).map((rep, index) => ({
      name: rep.name,
      territory: "—",
      quota: null,
      attained: rep.wonAmount,
      deals: rep.wonCount,
      badge: index === 0 ? "gold" : index === 1 ? "silver" : index === 2 ? "bronze" : null,
    }));

    const quotaTrend = (data.quotaTrend || []).map((point) => ({
      month: point.month,
      attained: point.attained,
      quota: null,
    }));

    const sparkData = quotaTrend.map((point) => point.attained);
    const deltaPercent = () => {
      if (quotaTrend.length < 2) return "—";
      const prev = quotaTrend[quotaTrend.length - 2]?.attained || 0;
      const last = quotaTrend[quotaTrend.length - 1]?.attained || 0;
      if (!prev) return "—";
      return `${((last - prev) / prev) * 100 >= 0 ? "+" : ""}${((last - prev) / prev * 100).toFixed(1)}%`;
    };

    const metrics = data.metrics || {};
    const metricsStrip = [
      {
        id: "pipeline",
        label: "Pipeline Value",
        value: formatInrCompact(metrics.pipelineValue || 0),
        change: deltaPercent(),
        positive: true,
        icon: "IndianRupee",
        sparkData,
      },
      {
        id: "winrate",
        label: "Win Rate",
        value: `${Number(metrics.winRate || 0).toFixed(1)}%`,
        change: "—",
        positive: true,
        icon: "Trophy",
        sparkData: sparkData.map((n) => Number((n % 100).toFixed(1))),
      },
      {
        id: "dealsize",
        label: "Avg Deal Size",
        value: formatInrCompact(metrics.avgDealSize || 0),
        change: "—",
        positive: true,
        icon: "Package",
        sparkData,
      },
      {
        id: "cycle",
        label: "Sales Cycle",
        value: `${Number(metrics.avgCycleDays || 0).toFixed(1)} days`,
        change: "—",
        positive: true,
        icon: "Clock",
        sparkData: sparkData.map((n) => Number((n % 60).toFixed(1))),
      },
    ];

    return {
      reps: data.reps || [],
      leaderboard,
      funnel: (data.funnel || []).map((f) => ({ ...f, label: STAGE_LABELS[f.stage] || "Prospecting" })),
      pipelineTable,
      metrics: metricsStrip,
      quotaTrend,
      velocity: data.velocity || { kpis: [], rings: [] },
      lastDataAtUtc: data.lastDataAtUtc || null,
    };
  },

  /** Customer Analytics widgets (metric cards, journey funnel, channels). */
  async getCustomers() {
    const data = await apiRequest("/api/reports/customers");

    const activity = data.activity || {};
    const asOf = formatLastDataUtc(data.lastDataAtUtc);
    const activityFeed = [
      {
        customer: "Inbox",
        action: `${activity.messagesInbound || 0} inbound · ${activity.messagesOutbound || 0} outbound messages`,
        type: "support",
        time: asOf,
        value: null,
      },
      {
        customer: "Calls",
        action: `${activity.callsTotal || 0} calls logged, ${activity.callsMissed || 0} missed`,
        type: "milestone",
        time: asOf,
        value: null,
      },
      {
        customer: "Conversations",
        action: `${activity.conversationsOpen || 0} open of ${activity.conversationsTotal || 0} total`,
        type: "expansion",
        time: asOf,
        value: null,
      },
    ];

    return {
      metricCards: (data.metricCards || []).map((card) => ({
        ...card,
        change: card.change || "live",
        changeType: card.changeType || "positive",
        breakdown: card.breakdown || [],
      })),
      journeyFunnel: data.journeyFunnel || [],
      activityFeed,
      acquisitionChannels: data.acquisitionChannels || {},
      satisfactionTrends: data.satisfactionTrends || [],
      segmentationMatrix: data.segmentationMatrix || [],
      retentionCohort: data.retentionCohort || [],
      churnSegments: data.churnSegments || [],
      atRiskAccounts: data.atRiskAccounts || [],
      topCompanies: data.topCompanies || [],
      lastDataAtUtc: data.lastDataAtUtc || null,
    };
  },

  /**
   * Full-data CSV export. Fetches the CSV with the auth header (the shared
   * client assumes JSON) and triggers a download named by the backend.
   */
  async exportDataset(dataset) {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/api/reports/export/${dataset}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      let message = `Request failed: ${response.status}`;
      try {
        const error = await response.json();
        if (error?.message) message = error.message;
      } catch {
        // Non-JSON error body — keep the generic message.
      }
      throw new ApiError(message, { status: response.status });
    }
    const text = await response.text();
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^";]+)"?/);
    const fileName = match?.[1] || `${dataset}-export.csv`;
    const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return fileName;
  },
};
